import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Send, Minimize2, MessageCircle, CheckCircle2, User, Bot } from "lucide-react";
import axios from "axios";
import MessageFormatter from "./MessageFormatter";
import OfflineForm from "./OfflineForm";
import { getVisitorInfo, getMinimalVisitorInfo, initVisitorTracking, trackPageVisit, hasGeoConsent, hasBeenAskedGeoConsent, setGeoConsentStatus, setHostPageTitle } from "../utils/visitorInfo";
import { widgetAPI, visitorTokenManager, widgetApiClient } from "../utils/api";

// Session status constants
const SESSION_STATUS = {
  ACTIVE: 'active',
  CLOSED: 'closed',
  EXPIRED: 'expired',
  WAITING_FOR_AGENT: 'waiting_for_agent',
  AGENT_ACTIVE: 'agent_active',
};
import { logger } from "../utils/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Token refresh interval (refresh 5 minutes before expiry for safety margin)
const TOKEN_REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes (tokens expire at 15 min)

// Helper function to get parent window origin for secure postMessage
const getParentOrigin = () => {
  // Try to get hostUrl from URL parameters (passed by widget-embed.js)
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const hostUrl = urlParams.get('hostUrl');
    if (hostUrl) {
      const url = new URL(decodeURIComponent(hostUrl));
      return url.origin;
    }
  } catch (e) {
    // URL parsing failed
  }

  // Try to get origin from document.referrer
  try {
    if (document.referrer) {
      const url = new URL(document.referrer);
      return url.origin;
    }
  } catch (e) {
    // Referrer parsing failed
  }

  // Fallback to '*' only if we can't determine the origin
  // This is less secure but ensures functionality
  return '*';
};

// Helper function to clear old session format data (runs synchronously)
// This clears legacy non-tenant-specific keys
const clearOldSessionFormatData = () => {
  // Check for any legacy keys and clear them
  const legacyKeys = [
    'chat_session_id',
    'visitor_token',
    'g6th_visitor_token',
    'g6th_visitor_id',
    'g6th_tenant_id'
  ];

  let cleared = false;
  legacyKeys.forEach(key => {
    if (localStorage.getItem(key)) {
      logger.log('[ChatWidget] SYNC: Clearing legacy key:', key);
      localStorage.removeItem(key);
      cleared = true;
    }
  });

  return cleared;
};

// Helper: Determine session end reason from messages
const determineSessionEndReason = (messages) => {
  const systemMessages = messages.filter(msg => msg.role === 'system');
  const lastSystemMsg = systemMessages[systemMessages.length - 1];

  if (lastSystemMsg && lastSystemMsg.message_metadata?.action === 'session_ended_by_visitor') {
    return 'visitor_closed';
  }
  if (lastSystemMsg && lastSystemMsg.content?.includes('inactivity')) {
    return 'inactivity';
  }
  return 'hard_refresh';
};

// Helper: Transform messages for display (add timestamp as Date object)
const transformMessagesForDisplay = (messages) => {
  return messages.map(msg => ({
    ...msg,
    timestamp: new Date(msg.timestamp)
  }));
};

// Helper: Merge config with fallback for display_name
const mergeConfigWithDefaults = (prevConfig, newConfig) => {
  const mergedConfig = { ...prevConfig, ...newConfig };
  if (!mergedConfig.display_name && mergedConfig.company_name) {
    mergedConfig.display_name = mergedConfig.company_name;
  }
  return mergedConfig;
};

// Helper: Check if message is duplicate (by ID or content+role+timestamp proximity)
const isMessageDuplicate = (existingMessages, newMessage, timeWindowMs = 5000) => {
  return existingMessages.some(existing =>
    existing.id === newMessage.id ||
    (existing.content === newMessage.content &&
      existing.role === newMessage.role &&
      Math.abs(new Date(existing.timestamp).getTime() - new Date(newMessage.timestamp).getTime()) < timeWindowMs)
  );
};

// Helper: Check if main menu pattern matches user input
const isMainMenuRequest = (userMessage) => {
  const userMessageLower = userMessage.toLowerCase().trim();
  const mainMenuPatterns = ['main menu', 'mainmenu', 'show menu', 'menu', 'start over', 'go back to menu'];
  return mainMenuPatterns.some(pattern =>
    userMessageLower === pattern || userMessageLower.includes('main menu')
  );
};

// Helper: Get follow-up message based on flow choice
const getFollowUpMessageForChoice = (choice, config, userData) => {
  if (choice === "information") {
    return config?.information_prompt || "What would you like to know?";
  }
  if (choice === "sales") {
    return config?.sales_prompt || "What product or service are you interested in?";
  }
  if (choice === "agent") {
    if (hasCompleteUserData(userData)) {
      return `Welcome back, ${userData.name}! Let me connect you with an available agent.`;
    }
    return config?.agent_prompt || "I'll connect you with an agent. Please provide your name first.";
  }
  return "";
};

// Helper: Get option text for flow choice
const getOptionTextForChoice = (choice) => {
  const optionTextMap = {
    information: "I need information",
    sales: "I have a sales enquiry",
    agent: "Talk to an agent"
  };
  return optionTextMap[choice] || "";
};

// Helper: Data collection step configuration
const DATA_COLLECTION_STEPS = {
  1: {
    field: 'name',
    nextPrompt: "Thank you! What's your email address?",
    nextStep: 2,
    validate: (value) => value.length >= 1 && value.length <= 100,
    errorMessage: "Please enter a valid name (1-100 characters)."
  },
  2: {
    field: 'email',
    nextPrompt: "Great! And your phone number?",
    nextStep: 3,
    validate: (value) => /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{1,63}$/.test(value),
    errorMessage: "That doesn't look like a valid email address. Please enter a valid email (e.g., name@example.com)."
  },
  3: {
    field: 'phone',
    nextPrompt: "Almost done! Could you briefly describe what you'd like to discuss with our team?",
    nextStep: 4,
    validate: (value) => /^\d{7,15}$/.test(value.replace(/[\s\-\(\)\.\+]/g, '')),
    errorMessage: "Please enter a valid phone number (at least 7 digits). You can include country code if needed."
  }
};

// Helper: Extract error message from API response
const extractApiErrorMessage = (error, defaultMessage) => {
  if (error.response?.data?.detail) {
    const details = error.response.data.detail;
    if (Array.isArray(details) && details.length > 0) {
      const field = details[0].loc?.[1] || 'field';
      const msg = details[0].msg || 'Invalid input';
      return `There was an issue with your ${field}: ${msg}. Please try again.`;
    }
  }
  return defaultMessage;
};

// Helper: Check if user has complete data for agent request
const hasCompleteUserData = (userData) => {
  return userData.name && userData.email && userData.phone;
};

// =============================================================================
// Position, Size, and Style Configuration
// =============================================================================

/**
 * Position class mappings for widget placement
 */
const POSITION_CLASS_MAP = {
  "bottom-right": "bottom-8 right-8",
  "bottom-left": "bottom-8 left-8",
  "top-right": "top-8 right-8",
  "top-left": "top-8 left-8"
};

/**
 * Widget size configurations
 */
const WIDGET_SIZE_MAP = {
  small: { width: "w-[320px]", height: "h-[450px]" },
  medium: { width: "w-[420px]", height: "h-[650px]" },
  large: { width: "w-[500px]", height: "h-[750px]" }
};

/**
 * Border radius class mappings
 */
const BORDER_RADIUS_MAP = {
  square: "rounded-none",
  rounded: "rounded-3xl",
  pill: "rounded-[2rem]"
};

/**
 * Get position classes from config
 */
const getPositionClassesFromConfig = (config) => {
  const position = config?.position || "bottom-right";
  return POSITION_CLASS_MAP[position] || POSITION_CLASS_MAP["bottom-right"];
};

/**
 * Get widget size from config
 */
const getWidgetSizeFromConfig = (config) => {
  const size = config?.widget_size || "medium";
  return WIDGET_SIZE_MAP[size] || WIDGET_SIZE_MAP.medium;
};

/**
 * Get border radius class from config
 */
const getBorderRadiusFromConfig = (config) => {
  const radius = config?.border_radius || "rounded";
  return BORDER_RADIUS_MAP[radius] || BORDER_RADIUS_MAP.rounded;
};

/**
 * Get background style based on color style setting
 */
const getBackgroundStyleFromConfig = (config) => {
  const primaryColor = config?.primary_color || "var(--g6-primary)";
  const secondaryColor = config?.secondary_color || "var(--g6-secondary)";
  const colorStyle = config?.color_style || "solid";

  if (colorStyle === "gradient") {
    return {
      background: `linear-gradient(to bottom right, ${primaryColor}, ${secondaryColor})`
    };
  }
  return { backgroundColor: primaryColor };
};

// =============================================================================
// Token Refresh Cleanup Helper
// =============================================================================

/**
 * Clean up token refresh resources (interval and event listeners)
 */
const cleanupTokenRefresh = (tokenRefreshRef) => {
  if (!tokenRefreshRef.current) return;

  if (typeof tokenRefreshRef.current === 'object') {
    // New format with event listeners
    if (tokenRefreshRef.current.interval) {
      clearInterval(tokenRefreshRef.current.interval);
    }
    if (tokenRefreshRef.current.handleVisibilityChange) {
      document.removeEventListener('visibilitychange', tokenRefreshRef.current.handleVisibilityChange);
    }
    if (tokenRefreshRef.current.handleFocus) {
      window.removeEventListener('focus', tokenRefreshRef.current.handleFocus);
    }
  } else {
    // Old format - just an interval ID
    clearInterval(tokenRefreshRef.current);
  }
  tokenRefreshRef.current = null;
};

// =============================================================================
// Session Restoration Helpers
// =============================================================================

/**
 * Process active session from history response
 */
const processActiveSession = (activeSession, setSessionId, setWaitingForAgent, setMessages, setChatStarted, setMenuUsed) => {
  setSessionId(activeSession.session_id);

  if (activeSession.status === SESSION_STATUS.WAITING_FOR_AGENT) {
    setWaitingForAgent(true);
  }

  if (activeSession.messages && activeSession.messages.length > 0) {
    const loadedMessages = transformMessagesForDisplay(activeSession.messages);
    setMessages(loadedMessages);
    setChatStarted(true);
    setMenuUsed(true);
  }

  logger.log('[ChatWidget] Active session restored:', activeSession.session_id);
};

/**
 * Process expired session from history response
 */
const processExpiredSession = (session, setMessages, setChatStarted, setHistoryMessageCount, setSessionEndReason, setLastSessionExpired) => {
  if (session && session.messages && session.messages.length > 0) {
    const loadedMessages = transformMessagesForDisplay(session.messages);
    setMessages(loadedMessages);
    setHistoryMessageCount(loadedMessages.length);
    setSessionEndReason(determineSessionEndReason(session.messages));
  }
  // Always set chatStarted so the ExpiredSessionInput ("Let's Chat" button) renders
  // even if the expired session had no loadable messages
  setChatStarted(true);
  setLastSessionExpired(true);
};

// =============================================================================
// WebSocket Message Handlers
// =============================================================================

/**
 * Handle WebSocket auth response messages
 * @returns {boolean} true if message was handled (auth-related), false otherwise
 */
const handleWsAuthMessage = (data, setReconnectAttempts) => {
  if (data.type === 'auth_success') {
    logger.log('[ChatWidget] WebSocket authenticated successfully');
    setReconnectAttempts(0);
    return true;
  }
  if (data.type === 'auth_failed' || data.type === 'auth_required' || data.type === 'auth_timeout') {
    logger.error('[ChatWidget] WebSocket auth failed:', data.message);
    return true;
  }
  return false;
};

/**
 * Handle new message from WebSocket
 */
const handleWsNewMessage = (data, typingTimeoutRef, setIsTyping, setMessages) => {
  if (typingTimeoutRef.current) {
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = null;
  }
  setIsTyping(false);

  setMessages(prev => {
    if (isMessageDuplicate(prev, data.message)) return prev;
    return [...prev, {
      ...data.message,
      timestamp: new Date(data.message.timestamp)
    }];
  });
};

/**
 * Handle initial messages from WebSocket
 */
const handleWsInitialMessages = (data, setMessages, setChatStarted) => {
  logger.log('[ChatWidget] Received initial messages via WebSocket:', data.messages.length);
  setMessages(prev => {
    const newMessages = data.messages
      .filter(m => !isMessageDuplicate(prev, m))
      .map(m => ({ ...m, timestamp: new Date(m.timestamp) }));

    if (newMessages.length === 0) return prev;

    const combined = [...prev, ...newMessages];
    combined.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    return combined;
  });
  if (data.messages.length > 0) {
    setChatStarted(true);
  }
};

// =============================================================================
// Widget Initialization Helpers
// =============================================================================

/**
 * Set up tenant context from widget config or storage
 * @returns {string|null} The tenant ID that was set
 */
const setupTenantContext = (widgetConfig, storedContext) => {
  if (widgetConfig?.tenantId) {
    visitorTokenManager.setTenantContext(widgetConfig.tenantId);
    console.log('[initializeWidget] Set tenant context from config:', widgetConfig.tenantId);
    return widgetConfig.tenantId;
  }
  if (storedContext?.tenantId) {
    visitorTokenManager.setTenantContext(storedContext.tenantId);
    console.log('[initializeWidget] Set tenant context from storage:', storedContext.tenantId);
    return storedContext.tenantId;
  }
  return null;
};

/**
 * Validate existing token and restore session if valid
 * @returns {boolean} true if token was valid and session restored
 */
const validateAndRestoreExistingToken = async (restoreSession, setWidgetInitialized, startTokenRefresh) => {
  try {
    console.log('[initializeWidget] Found existing token, validating...');
    const validation = await widgetAPI.validateToken();
    console.log('[initializeWidget] Token validation result:', validation);

    if (validation.valid) {
      // If session is closed/archived, clear token so widget re-initializes with a fresh session
      const sessionStatus = validation.session_status;
      if (sessionStatus === 'closed' || sessionStatus === 'archived') {
        console.log('[initializeWidget] Session is', sessionStatus, '- clearing token for re-initialization');
        visitorTokenManager.clearToken();
        return false;
      }

      console.log('[initializeWidget] Token is valid, restoring session...');
      setWidgetInitialized(true);

      const currentSessionId = visitorTokenManager.getSessionId();
      console.log('[initializeWidget] Restoring session, stored sessionId:', currentSessionId);

      try {
        await restoreSession(currentSessionId);
      } catch (restoreError) {
        console.error('[initializeWidget] Error restoring session:', restoreError);
      }

      // NOTE: setSessionLoaded is NOT called here anymore - it is called by
      // the caller AFTER tenant config is loaded, so the widget stays in
      // loading state until config (colors, theme, etc.) is ready.
      startTokenRefresh();
      return true;
    }
    // Token expired - clear it so we don't retry validation on next load
    console.log('[initializeWidget] Token expired, clearing for re-initialization');
    visitorTokenManager.clearToken();
  } catch (error) {
    console.log('[initializeWidget] Existing token invalid, will re-initialize:', error);
    visitorTokenManager.clearToken();
  }
  return false;
};

/**
 * Handle successful widget initialization
 */
const handleInitSuccess = async (result, setWidgetInitialized, setConfig, setSessionId, startTokenRefresh, trackVisitorPresence, restoreSession, setIsAgentAvailable, setAgentOfflineConfig) => {
  logger.log('[ChatWidget] Widget initialized for tenant:', result.tenantId);
  setWidgetInitialized(true);

  if (result.config) {
    setConfig(prevConfig => {
      logger.log('[ChatWidget] Loaded tenant config from init:', result.config.company_name);
      return mergeConfigWithDefaults(prevConfig, result.config);
    });

    // Store agent availability info (AI chatbot is always online)
    if (result.config.is_agent_available === false) {
      logger.log('[ChatWidget] Agents are OFFLINE per schedule — agent handoff will show offline form');
      setIsAgentAvailable(false);
      setAgentOfflineConfig({
        heading: result.config.agent_offline_heading,
        description: result.config.agent_offline_description,
        nextAvailableAt: result.config.agent_next_available_at,
      });
    }
  }

  if (result.sessionId) {
    setSessionId(result.sessionId);
  }

  startTokenRefresh();

  try {
    await trackVisitorPresence();
  } catch (presenceError) {
    console.error('[ChatWidget] Error tracking presence:', presenceError);
  }

  try {
    await restoreSession(result.sessionId);
  } catch (restoreError) {
    console.error('[ChatWidget] Error restoring session:', restoreError);
  }
};

/**
 * Handle widget initialization error
 */
const handleInitError = (error, setInitError) => {
  logger.error('[ChatWidget] Widget initialization failed:', error);

  const errorDetail = error.response?.data?.detail;
  const errorMessage = typeof errorDetail === 'object' ? errorDetail.message : errorDetail;

  if (error.response?.status === 403) {
    setInitError(errorMessage || 'Widget is not allowed on this domain.');
    notifyParentOfError(errorMessage);
  } else if (error.response?.status === 401) {
    setInitError(errorMessage || 'Invalid widget configuration.');
    notifyParentOfError(errorMessage);
  } else {
    setInitError('Failed to initialize chat. Please try again later.');
  }
};

/**
 * Notify parent window of domain/config error
 */
const notifyParentOfError = (message) => {
  if (window.parent !== window) {
    window.parent.postMessage({ type: 'g6th-sense-domain-error', message }, getParentOrigin());
  }
};

// =============================================================================
// Menu Option Button Component (reduces JSX repetition and nesting)
// =============================================================================

/**
 * Reusable menu option button with hover effects
 */
const MenuOptionButton = ({ onClick, isDark, primaryColor, children, testId }) => {
  const baseClass = `w-full py-3 px-4 rounded-xl border-2 transition-all duration-200 text-center font-medium ${isDark
      ? 'bg-gray-600 border-gray-500 text-gray-100 hover:border-gray-400'
      : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-400'
    }`;

  const handleMouseEnter = (e) => {
    e.currentTarget.style.borderColor = primaryColor;
  };

  const handleMouseLeave = (e) => {
    e.currentTarget.style.borderColor = isDark ? '' : `${primaryColor}40`;
  };

  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={baseClass}
      style={{ borderColor: isDark ? undefined : `${primaryColor}40` }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </button>
  );
};

/**
 * Menu options panel with three choices
 */
const MenuOptionsPanel = ({ isDark, primaryColor, onChoice }) => (
  <div className="flex flex-col gap-3">
    <MenuOptionButton
      testId="option-information"
      onClick={() => onChoice("information")}
      isDark={isDark}
      primaryColor={primaryColor}
    >
      I need information
    </MenuOptionButton>
    <MenuOptionButton
      testId="option-sales"
      onClick={() => onChoice("sales")}
      isDark={isDark}
      primaryColor={primaryColor}
    >
      I have sales enquiry
    </MenuOptionButton>
    <MenuOptionButton
      testId="option-agent"
      onClick={() => onChoice("agent")}
      isDark={isDark}
      primaryColor={primaryColor}
    >
      Talk to an agent
    </MenuOptionButton>
  </div>
);

/**
 * Welcome card with logo, greeting, and menu options
 */
const WelcomeCard = ({ isDark, config, primaryColor, onChoice }) => (
  <div className="flex flex-col items-center animate-in fade-in duration-500">
    <div className={`${isDark ? 'bg-gray-700' : 'bg-white'} rounded-2xl shadow-lg p-6 w-full max-w-sm`}>
      {/* Logo */}
      <div className="flex justify-center mb-6">
        <img
          src={config?.logo_url || "/Logo.png"}
          alt={`${config?.display_name || "Company"} Logo`}
          className="h-16 object-contain"
        />
      </div>

      {/* Greeting Text */}
      <div className="text-center mb-6">
        <p className={`text-lg font-medium ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
          <span className="mr-2">&#128075;</span>
          Hello! I'm {config?.display_name || config?.company_name || "AI"}.
        </p>
        <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          What brings you here today?
        </p>
      </div>

      <MenuOptionsPanel isDark={isDark} primaryColor={primaryColor} onChoice={onChoice} />
    </div>
  </div>
);

/**
 * Restart menu bubble with interactive options
 */
const RestartMenuBubble = ({ isDark, config, primaryColor, onChoice }) => (
  <div className="flex justify-start animate-in fade-in slide-in-from-left-4 duration-500">
    <div className={`${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'} p-5 rounded-2xl rounded-bl-md shadow-md border max-w-[90%]`}>
      {/* Logo */}
      <div className="flex justify-center mb-4">
        {config?.logo_url ? (
          <img src={config.logo_url} alt="Logo" className="h-10 object-contain" />
        ) : (
          <div className="text-xl font-bold" style={{ color: primaryColor }}>
            {config?.company_name || 'Assistant'}
          </div>
        )}
      </div>

      {/* Greeting */}
      <div className="text-center mb-4">
        <p className={`text-lg font-medium ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
          👋 Hello! I'm {config?.company_name || 'AI'} Assistant.
        </p>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
          What brings you here today?
        </p>
      </div>

      <MenuOptionsPanel isDark={isDark} primaryColor={primaryColor} onChoice={onChoice} />
    </div>
  </div>
);

/**
 * Typing indicator component
 */
const TypingIndicator = ({ isDark, primaryColor, borderRadiusClass }) => (
  <div className="flex justify-start animate-in fade-in slide-in-from-left-4 duration-300">
    <div className={`${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'} p-4 ${borderRadiusClass} rounded-bl-md shadow-md border`}>
      <div className="flex items-center space-x-2">
        <Bot size={16} style={{ color: primaryColor }} />
        <div className="flex space-x-1">
          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor }}></div>
          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '0.2s' }}></div>
          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '0.4s' }}></div>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Follow-up question component
 */
const FollowUpQuestion = ({ isDark, primaryColor, backgroundStyle, borderRadiusClass, onYes, onNo }) => (
  <div className="flex flex-col items-start animate-in fade-in slide-in-from-left-4 duration-300">
    <div className={`${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'} p-4 ${borderRadiusClass} rounded-bl-md shadow-md border`}>
      <div className="flex items-center gap-2 mb-2">
        <Bot size={16} style={{ color: primaryColor }} />
        <p className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>AI Assistant</p>
      </div>
      <p className={`text-sm ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
        Do you need any further information?
      </p>
    </div>
    <div className="flex gap-2 mt-2 ml-2">
      <button
        onClick={onYes}
        className="px-4 py-2 text-sm font-medium rounded-lg transition-all hover:opacity-90"
        style={{ ...(backgroundStyle || { backgroundColor: primaryColor }), color: 'white' }}
      >
        Yes
      </button>
      <button
        onClick={onNo}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all border hover:opacity-90 ${isDark ? 'bg-gray-600 text-white border-gray-500' : 'bg-gray-100 text-gray-700 border-gray-300'}`}
      >
        No. I am good.
      </button>
    </div>
  </div>
);

/**
 * Session closed card with "Let's Chat" button
 */
const SessionClosedCard = ({ isDark, primaryColor, backgroundStyle, onRestart }) => (
  <div className="flex flex-col items-center animate-in fade-in duration-500 py-4">
    <div className={`${isDark ? 'bg-gray-700' : 'bg-white'} rounded-2xl shadow-lg p-6 w-full max-w-sm text-center`}>
      <p className={`text-lg font-medium mb-4 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
        Hello. How may I help you?
      </p>
      <button
        onClick={onRestart}
        className="px-6 py-3 text-sm font-medium rounded-xl transition-all hover:opacity-90"
        style={{ ...(backgroundStyle || { backgroundColor: primaryColor }), color: 'white' }}
      >
        Let's Chat
      </button>
    </div>
  </div>
);

/**
 * Agent timeout options component
 */
const AgentTimeoutOptions = ({ isDark, onKeepWaiting, onTransferToAI, backgroundStyle }) => (
  <div className={`p-4 border-t-2 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'}`}>
    <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-amber-800'}`}>
      It's been over 2 minutes and an agent hasn't responded yet. What would you like to do?
    </p>
    <div className="flex gap-2">
      <button
        onClick={onKeepWaiting}
        className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${isDark
          ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
          : 'border-amber-300 text-amber-800 hover:bg-amber-100'
          }`}
      >
        Keep waiting
      </button>
      <button
        onClick={onTransferToAI}
        className="flex-1 px-3 py-2 text-sm rounded-lg text-white transition-colors hover:opacity-90"
        style={backgroundStyle}
      >
        Talk to AI instead
      </button>
    </div>
  </div>
);

/**
 * Expired session input area with "Let's Chat" button
 */
const ExpiredSessionInput = ({ isDark, primaryColor, backgroundStyle, onRestart }) => (
  <div className="flex flex-col items-center">
    <div className={`w-full mb-4 p-4 rounded-2xl ${isDark ? 'bg-gray-700 text-gray-100' : 'bg-gray-50 text-gray-800'}`}>
      <p className={`text-center text-lg font-medium ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
        Hello. How may I help you?
      </p>
    </div>
    <button
      onClick={onRestart}
      className="w-full py-3.5 text-sm font-medium rounded-2xl transition-all hover:opacity-90 shadow-lg hover:shadow-xl"
      style={{ ...(backgroundStyle || { backgroundColor: primaryColor }), color: 'white' }}
    >
      Let's Chat
    </button>
    <p className="text-xs text-gray-400 mt-2 text-center">
      Powered by <span className="font-semibold">G6th Sense</span>
    </p>
  </div>
);

// =============================================================================
// Session Restoration Helpers (Extended)
// =============================================================================

/**
 * Handle history response with active session
 */
const handleActiveSessionFromHistory = (historyResponse, setSessionId, setWaitingForAgent, setMessages, setChatStarted, setMenuUsed) => {
  const activeSession = historyResponse.sessions.find(s =>
    s.is_current_session && s.status !== SESSION_STATUS.CLOSED
  );

  if (activeSession) {
    processActiveSession(activeSession, setSessionId, setWaitingForAgent, setMessages, setChatStarted, setMenuUsed);
    return true;
  }
  return false;
};

/**
 * Handle history response when last session expired.
 * Loads ALL sessions' messages (oldest first) with dividers between them
 * so the returning visitor sees their complete chat history.
 */
const handleExpiredSessionFromHistory = (historyResponse, setMessages, setChatStarted, setHistoryMessageCount, setSessionEndReason, setLastSessionExpired) => {
  const allMessages = [];
  // Sessions come in desc order (newest first) - reverse to show oldest first
  const sessionsOldestFirst = [...historyResponse.sessions].reverse();

  sessionsOldestFirst.forEach((session, idx) => {
    if (!session.messages || session.messages.length === 0) return;

    // Add a session divider between sessions (not before the very first)
    if (allMessages.length > 0) {
      allMessages.push({
        id: `session-divider-${session.session_id || idx}`,
        role: 'system',
        content: 'Previous conversation ended',
        timestamp: session.created_at ? new Date(session.created_at) : new Date(),
      });
    }

    const transformed = transformMessagesForDisplay(session.messages);
    allMessages.push(...transformed);
  });

  if (allMessages.length > 0) {
    setMessages(allMessages);
    setHistoryMessageCount(allMessages.length);
    // Use the most recent session to determine end reason
    const mostRecentSession = historyResponse.sessions[0];
    setSessionEndReason(determineSessionEndReason(mostRecentSession.messages));
  }

  // Always set chatStarted + lastSessionExpired so the "Let's Chat" button renders
  setChatStarted(true);
  setLastSessionExpired(true);
};

/**
 * Fallback session restoration when chat history API fails
 */
const fallbackSessionRestore = async (storedSessionId, setSessionId, setFlowType, setUserData, setWaitingForAgent, setMessages, setChatStarted, setLastSessionExpired, setSessionEndReason, setHistoryMessageCount) => {
  if (!storedSessionId) {
    logger.log('[ChatWidget] No stored session ID for fallback, starting fresh');
    return;
  }

  const [sessionResponse, messagesResponse] = await Promise.all([
    widgetApiClient.get(`/api/session/${storedSessionId}`),
    widgetApiClient.get(`/api/session/${storedSessionId}/messages`)
  ]);

  if (!sessionResponse.data?.session_id) return;

  const sessionStatus = sessionResponse.data.status;
  const isExpired = sessionStatus === SESSION_STATUS.CLOSED || sessionStatus === SESSION_STATUS.EXPIRED;

  if (isExpired) {
    // Session is closed/expired - show "Let's Chat" button
    if (messagesResponse.data?.messages?.length > 0) {
      const loadedMessages = transformMessagesForDisplay(messagesResponse.data.messages);
      setMessages(loadedMessages);
      setChatStarted(true);
      setHistoryMessageCount(loadedMessages.length);
      setSessionEndReason(determineSessionEndReason(messagesResponse.data.messages));
    } else {
      setChatStarted(true);
    }
    setLastSessionExpired(true);
    logger.log('[ChatWidget] Expired session restored via fallback:', storedSessionId);
    return;
  }

  // Active session - restore normally
  setSessionId(sessionResponse.data.session_id);
  setFlowType(sessionResponse.data.flow_type);

  if (sessionResponse.data.user_data) {
    setUserData(sessionResponse.data.user_data);
  }

  if (sessionResponse.data.status === SESSION_STATUS.WAITING_FOR_AGENT) {
    setWaitingForAgent(true);
  }

  if (messagesResponse.data?.messages?.length > 0) {
    setMessages(transformMessagesForDisplay(messagesResponse.data.messages));
    setChatStarted(true);
  }

  logger.log('[ChatWidget] Session restored via fallback:', storedSessionId);
};

// =============================================================================
// Token Refresh Helpers
// =============================================================================

/**
 * Attempt to re-initialize widget after token refresh failure
 * @returns {boolean} true if re-initialization succeeded
 */
const attemptReInitialization = async (getWidgetConfig, setTokenVersion, setConfig, setWidgetInitialized) => {
  const widgetConfig = getWidgetConfig();
  if (!widgetConfig?.widgetPublicKey) {
    setWidgetInitialized(false);
    return false;
  }

  try {
    const existingVisitorId = visitorTokenManager.getVisitorId();
    const result = await widgetAPI.initialize(widgetConfig.widgetPublicKey, existingVisitorId);

    if (result.success) {
      console.log('[ChatWidget] Re-initialized after token refresh failure');
      setTokenVersion(v => v + 1);
      if (result.config) {
        setConfig(prevConfig => mergeConfigWithDefaults(prevConfig, result.config));
      }
      return true;
    }
    console.error('[ChatWidget] Re-initialization failed');
    setWidgetInitialized(false);
  } catch (reinitError) {
    console.error('[ChatWidget] Re-initialization error:', reinitError);
    setWidgetInitialized(false);
  }
  return false;
};

// =============================================================================
// WebSocket Connection Helpers
// =============================================================================

/**
 * Handle WebSocket token expired event
 */
const handleWsTokenExpired = (setTokenVersion) => {
  logger.log('[ChatWidget] WebSocket received token_expired, refreshing...');
  widgetAPI.refreshToken()
    .then(() => setTokenVersion(v => v + 1))
    .catch(err => logger.error('[ChatWidget] Token refresh failed after WS expiry:', err));
};

/**
 * Handle WebSocket close with auth error - refresh token and reconnect
 */
const handleWsAuthErrorClose = (connect, wsReconnectTimeoutRef) => {
  logger.log('[ChatWidget] WebSocket auth error, refreshing token...');
  widgetAPI.refreshToken()
    .then(() => {
      logger.log('[ChatWidget] Token refreshed, reconnecting WebSocket...');
      wsReconnectTimeoutRef.current = setTimeout(connect, 500);
    })
    .catch(err => {
      logger.error('[ChatWidget] Token refresh failed:', err);
      wsReconnectTimeoutRef.current = setTimeout(connect, 5000);
    });
};

/**
 * Check if WebSocket close code indicates auth error
 */
const isWsAuthError = (code) => {
  return code === 4003 || code === 403 || code === 1008;
};

// =============================================================================
// Streaming Response Helpers
// =============================================================================

/**
 * Parse SSE lines from a chunk and extract valid data
 * @returns {Array} Array of parsed data objects
 */
const parseSSELines = (chunk) => {
  const lines = chunk.split('\n');
  const parsedData = [];

  for (const line of lines) {
    if (!line.startsWith('data: ')) continue;

    try {
      const data = JSON.parse(line.slice(6));
      parsedData.push(data);
    } catch {
      logger.warn('[ChatWidget] Failed to parse SSE data:', line);
    }
  }

  return parsedData;
};

/**
 * Build fetch headers with optional auth token
 */
const buildStreamHeaders = () => {
  const headers = { 'Content-Type': 'application/json' };
  const token = visitorTokenManager.getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// =============================================================================
// Message Rendering Helper Components (to reduce JSX nesting depth)
// =============================================================================

/**
 * Get session end reason text
 */
const getSessionEndReasonText = (sessionEndReason) => {
  if (sessionEndReason === 'visitor_closed') {
    return 'Visitor closed the chat. Session ended.';
  }
  if (sessionEndReason === 'inactivity') {
    return 'Chat ended automatically due to your inactivity. Feel free to start it again.';
  }
  return 'Chat session ended';
};

/**
 * Session Divider component - shows session end notices
 */
const SessionDivider = ({ isDark, text }) => (
  <div className="flex items-center justify-center w-full my-3">
    <div className={`flex-1 h-px ${isDark ? 'bg-gray-600' : 'bg-gray-200'}`}></div>
    <span className={`px-3 py-1 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
      {text}
    </span>
    <div className={`flex-1 h-px ${isDark ? 'bg-gray-600' : 'bg-gray-200'}`}></div>
  </div>
);

/**
 * Bot Greeting Bubble component
 */
const BotGreetingBubble = ({ isDark, primaryColor }) => (
  <div className="flex justify-start mb-4">
    <div className="flex items-start gap-3 max-w-[85%]">
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: primaryColor }}
      >
        <Bot size={16} className="text-white" />
      </div>
      <div className={`${isDark ? 'bg-gray-700 text-gray-100' : 'bg-white text-gray-800'} p-4 rounded-2xl rounded-bl-md shadow-md`}>
        <p className="text-sm leading-relaxed">
          Hello. How may I help you?
        </p>
      </div>
    </div>
  </div>
);

/**
 * Static Menu Option component
 */
const StaticMenuOption = ({ isDark, text }) => (
  <div className={`w-full py-3 px-4 rounded-xl border-2 text-center font-medium ${isDark
    ? 'bg-gray-600 border-gray-500 text-gray-100'
    : 'bg-gray-50 border-gray-200 text-gray-700'
    }`}>
    {text}
  </div>
);

/**
 * Static Menu Bubble component (displayed after history messages)
 */
const StaticMenuBubble = ({ isDark, config, primaryColor }) => (
  <div className="flex justify-start mb-4">
    <div className={`${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'} p-5 rounded-2xl rounded-bl-md shadow-md border max-w-[90%]`}>
      {/* Logo */}
      <div className="flex justify-center mb-4">
        {config?.logo_url ? (
          <img src={config.logo_url} alt="Logo" className="h-10 object-contain" />
        ) : (
          <div className="text-xl font-bold" style={{ color: primaryColor }}>
            {config?.company_name || 'Assistant'}
          </div>
        )}
      </div>

      {/* Greeting */}
      <div className="text-center mb-4">
        <p className={`text-lg font-medium ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
          👋 Hello! I&apos;m {config?.company_name || 'AI'} Assistant.
        </p>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
          What brings you here today?
        </p>
      </div>

      {/* Options - Static display */}
      <div className="flex flex-col gap-2">
        <StaticMenuOption isDark={isDark} text="I need information" />
        <StaticMenuOption isDark={isDark} text="I have sales enquiry" />
        <StaticMenuOption isDark={isDark} text="Talk to an agent" />
      </div>
    </div>
  </div>
);

/**
 * System Message component
 */
const SystemMessage = ({ msg, isDark }) => (
  <div
    data-testid={`message-${msg.role}`}
    className="flex items-center justify-center w-full my-3 animate-in fade-in duration-300"
  >
    <div className={`flex-1 h-px ${isDark ? 'bg-gray-600' : 'bg-gray-200'}`}></div>
    <span className={`px-3 py-1 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
      {msg.content}
    </span>
    <div className={`flex-1 h-px ${isDark ? 'bg-gray-600' : 'bg-gray-200'}`}></div>
  </div>
);

/**
 * Message Header component (for agent/assistant messages)
 */
const MessageHeader = ({ role, isDark, primaryColor, agentName }) => {
  if (role === "agent") {
    return (
      <div className="flex items-center gap-2 mb-2">
        <User size={16} style={{ color: primaryColor }} />
        <p className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
          {agentName || "Support Agent"}
        </p>
      </div>
    );
  }
  if (role === "assistant") {
    return (
      <div className="flex items-center gap-2 mb-2">
        <Bot size={16} style={{ color: primaryColor }} />
        <p className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>AI Assistant</p>
      </div>
    );
  }
  return null;
};

/**
 * Message Content component
 */
const MessageContent = ({ msg, primaryColor }) => {
  if (msg.role === "assistant" || msg.role === "agent") {
    return (
      <div className="text-sm leading-relaxed">
        <MessageFormatter content={msg.content} />
        {msg.isStreaming && (
          <span className="inline-block w-2 h-4 ml-1 animate-pulse" style={{ backgroundColor: primaryColor }}></span>
        )}
      </div>
    );
  }
  return (
    <p className="text-sm whitespace-pre-wrap leading-relaxed">
      {msg.content}
    </p>
  );
};

/**
 * Message Timestamp component
 */
const MessageTimestamp = ({ msg }) => (
  <div className="flex items-center justify-end gap-2 mt-2">
    <span className="text-xs opacity-60">
      {msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
    </span>
    {msg.role === "user" && (
      <CheckCircle2 size={12} className="opacity-60" />
    )}
  </div>
);

const ChatWidget = ({ embedded = true, tenantId: propTenantId, widgetPublicKey: propWidgetKey }) => {
  logger.log('[ChatWidget] Component rendering - props:', { embedded, propTenantId, propWidgetKey });

  // SYNCHRONOUS cleanup - runs BEFORE any hooks initialize
  // This must be at the very top before useState calls that might read localStorage
  const [oldDataCleared] = useState(() => clearOldSessionFormatData());

  const [isOpen, setIsOpen] = useState(!embedded);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [flowType, setFlowType] = useState(null);
  const [isCollectingData, setIsCollectingData] = useState(false);
  const [dataStep, setDataStep] = useState(0);
  const [userData, setUserData] = useState({ name: "", email: "", phone: "", context: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [chatStarted, setChatStarted] = useState(false);
  const [waitingForAgent, setWaitingForAgent] = useState(false);
  const [agentTimeoutExceeded, setAgentTimeoutExceeded] = useState(false);
  const [secondsWaiting, setSecondsWaiting] = useState(0);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [showMainMenu, setShowMainMenu] = useState(false);

  // Chat history state
  const [chatHistory, setChatHistory] = useState([]); // All historical sessions
  const [lastSessionExpired, setLastSessionExpired] = useState(false); // Shows expired session message
  const [sessionEndReason, setSessionEndReason] = useState(null); // 'visitor_closed', 'inactivity', or 'hard_refresh'
  const [showingHistory, setShowingHistory] = useState(false); // Currently viewing history
  const [showRestartMenu, setShowRestartMenu] = useState(false); // Shows interactive menu bubble
  const [menuUsed, setMenuUsed] = useState(false); // Track if menu was used (to show static version)
  const [showInitialMenu, setShowInitialMenu] = useState(false); // Show static menu after first flow selection (not on restore)
  const [historyMessageCount, setHistoryMessageCount] = useState(0); // Track how many messages are from history

  // Widget initialization state
  const [widgetInitialized, setWidgetInitialized] = useState(false);
  const [initError, setInitError] = useState(null);

  // Agent availability state (schedule-based — AI chatbot is always online)
  const [isAgentAvailable, setIsAgentAvailable] = useState(true);
  const [agentOfflineConfig, setAgentOfflineConfig] = useState(null);
  const [showAgentOfflineForm, setShowAgentOfflineForm] = useState(false);

  // Token version for WebSocket reconnection on token refresh
  const [tokenVersion, setTokenVersion] = useState(0);

  // GDPR: Geolocation consent state
  const [showGeoConsent, setShowGeoConsent] = useState(() => {
    // Show consent dialog if not asked before and widget is embedded
    return embedded && !hasBeenAskedGeoConsent();
  });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const agentCheckIntervalRef = useRef(null);
  const tokenRefreshIntervalRef = useRef(null);
  const wsRef = useRef(null);
  const wsReconnectTimeoutRef = useRef(null);

  // Clear stale session data - now just clears legacy keys
  // Tenant-specific keys are handled automatically by visitorTokenManager
  const clearStaleSessionData = useCallback((newTenantId) => {
    // Set new tenant context
    if (newTenantId) {
      visitorTokenManager.setTenantContext(newTenantId);
    }

    // Clear any legacy (non-tenant-specific) keys
    visitorTokenManager.clearLegacyKeys();

    logger.log('[ChatWidget] Cleared stale data, set tenant context:', newTenantId);
  }, []);

  // Get widget config from props, URL params, window config, or stored values
  const getWidgetConfig = useCallback(() => {
    // Priority 1: Props passed directly
    if (propTenantId && propWidgetKey) {
      // Clear stale data if tenant changed
      clearStaleSessionData(propTenantId);
      return { tenantId: propTenantId, widgetPublicKey: propWidgetKey };
    }

    // Priority 2: URL query parameters (for preview iframe)
    const urlParams = new URLSearchParams(window.location.search);
    const urlTenantId = urlParams.get('tenantId');
    const urlWidgetKey = urlParams.get('widgetPublicKey');
    if (urlTenantId && urlWidgetKey) {
      // Clear stale data if tenant changed
      clearStaleSessionData(urlTenantId);
      return { tenantId: urlTenantId, widgetPublicKey: urlWidgetKey };
    }

    // Priority 3: Window config (from embed script)
    if (window.G6thSenseConfig?.tenantId && window.G6thSenseConfig?.widgetPublicKey) {
      // Clear stale data if tenant changed
      clearStaleSessionData(window.G6thSenseConfig.tenantId);
      return {
        tenantId: window.G6thSenseConfig.tenantId,
        widgetPublicKey: window.G6thSenseConfig.widgetPublicKey
      };
    }

    // Priority 4: Stored tenant ID (for widget preview or already initialized)
    const storedTenantId = visitorTokenManager.getTenantId();
    logger.log('[getWidgetConfig] Stored tenant ID:', storedTenantId);

    if (storedTenantId) {
      // IMPORTANT: Set tenant context BEFORE checking hasToken()
      // On hard refresh, _currentTenantId is null, so hasToken() would use wrong key
      visitorTokenManager.setTenantContext(storedTenantId);

      const hasToken = visitorTokenManager.hasToken();
      logger.log('[getWidgetConfig] Has token after setting context:', hasToken);

      if (hasToken) {
        // Check for old session format even with stored tenant
        const storedSessionId = localStorage.getItem('chat_session_id');
        if (storedSessionId && storedSessionId.startsWith('session-')) {
          clearStaleSessionData(storedTenantId);
          return null; // Force re-initialization
        }
        logger.log('[getWidgetConfig] Returning stored tenant config');
        return { tenantId: storedTenantId, widgetPublicKey: null }; // Already initialized
      }
    }

    logger.log('[getWidgetConfig] No valid config found, returning null');
    return null;
  }, [propTenantId, propWidgetKey, clearStaleSessionData]);

  const scrollToBottom = (instant = false) => {
    messagesEndRef.current?.scrollIntoView({ behavior: instant ? "instant" : "smooth" });
  };

  // Scroll to bottom when messages change or typing indicator appears
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Scroll to bottom when widget is opened (instant scroll, no animation)
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => scrollToBottom(true), 100);
    }
  }, [isOpen]);

  // Cleanup typing timeout, agent check interval, token refresh, and WebSocket on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      if (agentCheckIntervalRef.current) {
        clearInterval(agentCheckIntervalRef.current);
        agentCheckIntervalRef.current = null;
      }
      // Use helper for token refresh cleanup
      cleanupTokenRefresh(tokenRefreshIntervalRef);
      if (wsReconnectTimeoutRef.current) {
        clearTimeout(wsReconnectTimeoutRef.current);
        wsReconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  // Check agent status when waiting for agent
  useEffect(() => {
    logger.log('[ChatWidget] Agent status useEffect triggered:', { sessionId, waitingForAgent, widgetInitialized, agentTimeoutExceeded });

    if (!sessionId || !waitingForAgent || !widgetInitialized) {
      logger.log('[ChatWidget] Agent status check skipped - conditions not met');
      if (agentCheckIntervalRef.current) {
        clearInterval(agentCheckIntervalRef.current);
        agentCheckIntervalRef.current = null;
      }
      return;
    }

    const checkAgentStatus = async () => {
      try {
        logger.log('[ChatWidget] Checking agent status for session:', sessionId);
        // Use authenticated client for agent status check
        const response = await widgetApiClient.get(`/api/session/${sessionId}/agent-status`);
        const { status, agent_responded, timeout_exceeded, seconds_waiting } = response.data;

        logger.log('[ChatWidget] Agent status response:', { status, agent_responded, timeout_exceeded, seconds_waiting });

        setSecondsWaiting(seconds_waiting);

        // If agent responded or status changed, stop waiting
        if (agent_responded || status === "agent_active") {
          logger.log('[ChatWidget] Agent responded or active, stopping wait');
          setWaitingForAgent(false);
          setAgentTimeoutExceeded(false);
          if (agentCheckIntervalRef.current) {
            clearInterval(agentCheckIntervalRef.current);
            agentCheckIntervalRef.current = null;
          }
          return;
        }

        // If status is no longer waiting for agent (e.g., closed, active)
        if (status !== "waiting_for_agent") {
          logger.log('[ChatWidget] Status changed to:', status, '- stopping wait');
          setWaitingForAgent(false);
          setAgentTimeoutExceeded(false);
          if (agentCheckIntervalRef.current) {
            clearInterval(agentCheckIntervalRef.current);
            agentCheckIntervalRef.current = null;
          }
          return;
        }

        // Set timeout exceeded flag
        if (timeout_exceeded && !agentTimeoutExceeded) {
          logger.log('[ChatWidget] Timeout exceeded! Showing options dialog');
          setAgentTimeoutExceeded(true);
        }
      } catch (error) {
        logger.error('[ChatWidget] Error checking agent status:', error);
      }
    };

    // Check immediately
    logger.log('[ChatWidget] Starting agent status polling');
    checkAgentStatus();

    // Then check every 10 seconds
    agentCheckIntervalRef.current = setInterval(checkAgentStatus, 10000);

    return () => {
      if (agentCheckIntervalRef.current) {
        logger.log('[ChatWidget] Cleaning up agent status interval');
        clearInterval(agentCheckIntervalRef.current);
        agentCheckIntervalRef.current = null;
      }
    };
  }, [sessionId, waitingForAgent, agentTimeoutExceeded, widgetInitialized]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, flowType]);

  // Listen for page-change messages from the parent page (widget-embed.js)
  // This keeps page title and URL in sync when the parent site is a SPA
  useEffect(() => {
    const handleParentMessage = (event) => {
      if (!event.data || event.data.type !== 'g6th-sense-page-change') return;
      const { url, title } = event.data;
      if (title) setHostPageTitle(title);
      if (url) trackPageVisit(url, title || null);
    };
    window.addEventListener('message', handleParentMessage);
    return () => window.removeEventListener('message', handleParentMessage);
  }, []);

  // Widget initialization with visitor token
  useEffect(() => {
    // Initialize visitor tracking FIRST (synchronous)
    initVisitorTracking();

    const initializeWidget = async () => {
      logger.log('[initializeWidget] Starting widget initialization...');

      // STEP 1: Restore and set up tenant context
      const storedContext = visitorTokenManager.restoreContextFromStorage();
      logger.log('[initializeWidget] Restored context from storage:', storedContext);

      const widgetConfig = getWidgetConfig();
      logger.log('[initializeWidget] Widget config:', widgetConfig);

      setupTenantContext(widgetConfig, storedContext);
      visitorTokenManager.clearLegacyKeys();

      // STEP 2: Try to validate existing token
      const hasToken = visitorTokenManager.hasToken();
      logger.log('[initializeWidget] Has token:', hasToken);

      if (hasToken) {
        const tokenRestored = await validateAndRestoreExistingToken(
          restoreSession, setWidgetInitialized, startTokenRefresh
        );
        if (tokenRestored) {
          // Load tenant-specific config (token restore path skips init which normally provides config)
          // Must await to ensure tenant config is loaded before rendering with stale/global defaults
          await fetchTenantConfig();
          // Re-register visitor presence so returning visitors appear in admin panel
          // This is needed because /visitor/leave sets visitor_active=false when they left
          await trackVisitorPresence();
          // Mark session as loaded AFTER config is ready, so the widget doesn't
          // flash with default colors before tenant config arrives
          setSessionLoaded(true);
          return;
        }
      }

      // STEP 3: Check for widget key
      if (!widgetConfig?.widgetPublicKey) {
        logger.error('[initializeWidget] No widget configuration found - widgetConfig:', widgetConfig);

        // FALLBACK: If we have a stored visitor ID, try to re-initialize using stored tenant
        if (storedContext?.visitorId && storedContext?.tenantId) {
          logger.log('[initializeWidget] Attempting fallback with stored visitor ID...');
          // The widget embed script should provide the key, so this is likely a configuration issue
        }

        setInitError('Widget not configured. Please contact the site administrator.');
        setSessionLoaded(true);
        return;
      }

      logger.log('[initializeWidget] Initializing with widget key...');

      try {
        const existingVisitorId = visitorTokenManager.getVisitorId();
        const result = await widgetAPI.initialize(widgetConfig.widgetPublicKey, existingVisitorId);

        if (result.success) {
          await handleInitSuccess(
            result, setWidgetInitialized, setConfig, setSessionId,
            startTokenRefresh, trackVisitorPresence, restoreSession,
            setIsAgentAvailable, setAgentOfflineConfig
          );
        }
      } catch (error) {
        handleInitError(error, setInitError);
      }

      setSessionLoaded(true);
    };

    const restoreSession = async (storedSessionId) => {
      try {
        const historyResponse = await widgetAPI.getChatHistory(true);

        if (!historyResponse.success || !historyResponse.sessions?.length) {
          logger.log('[ChatWidget] No chat history found, starting fresh');
          return;
        }

        logger.log('[ChatWidget] Loaded chat history:', historyResponse.total_sessions, 'sessions,', historyResponse.total_messages, 'total messages');
        setChatHistory(historyResponse.sessions);

        // Handle expired last session
        if (historyResponse.last_session_expired) {
          logger.log('[ChatWidget] Last session was expired/closed, showing welcome back message');
          handleExpiredSessionFromHistory(historyResponse, setMessages, setChatStarted, setHistoryMessageCount, setSessionEndReason, setLastSessionExpired);
          return;
        }

        // Try to restore active session
        const hasActiveSession = handleActiveSessionFromHistory(
          historyResponse, setSessionId, setWaitingForAgent, setMessages, setChatStarted, setMenuUsed
        );

        if (!hasActiveSession) {
          // No active session but we have history - show as expired
          logger.log('[ChatWidget] No active session found, showing history as expired');
          handleExpiredSessionFromHistory(historyResponse, setMessages, setChatStarted, setHistoryMessageCount, setSessionEndReason, setLastSessionExpired);
        }
      } catch (error) {
        logger.log('[ChatWidget] Chat history fetch failed, trying direct session restore:', error);

        try {
          await fallbackSessionRestore(storedSessionId, setSessionId, setFlowType, setUserData, setWaitingForAgent, setMessages, setChatStarted, setLastSessionExpired, setSessionEndReason, setHistoryMessageCount);
        } catch (fallbackError) {
          if (fallbackError.response?.status === 404) {
            logger.log('[ChatWidget] Session not found, starting fresh');
          } else {
            logger.error('[ChatWidget] Error loading session:', fallbackError);
          }
          visitorTokenManager.clearToken();
        }
      }
    };

    const trackVisitorPresence = async () => {
      try {
        const visitorInfo = await getVisitorInfo();
        const visitorId = visitorTokenManager.getVisitorId();
        const tenantId = visitorTokenManager.getTenantId();

        logger.log('[ChatWidget] Tracking visitor presence:', { visitorId, tenantId });

        // Track visitor presence using authenticated client
        const response = await widgetApiClient.post('/api/visitor/presence', {
          visitor_id: visitorId,
          visitor_info: visitorInfo
        });

        logger.log('[ChatWidget] Visitor presence tracked successfully:', response.data);
      } catch (error) {
        logger.error('[ChatWidget] Error tracking visitor presence:', error.response?.data || error);
      }
    };

    const startTokenRefresh = () => {
      // Clear any existing interval
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }

      // Function to refresh the visitor token
      const refreshVisitorToken = async () => {
        try {
          const result = await widgetAPI.refreshToken();
          if (result.success) {
            logger.log('[ChatWidget] Token refreshed successfully');
            setTokenVersion(v => v + 1);
            return true;
          }
        } catch (error) {
          logger.error('[ChatWidget] Token refresh failed:', error);
          setWidgetInitialized(false);
          visitorTokenManager.clearToken();

          // Try to re-initialize with widget key using helper
          const reInitSuccess = await attemptReInitialization(
            getWidgetConfig, setTokenVersion, setConfig, setWidgetInitialized
          );
          return reInitSuccess;
        }
        return false;
      };

      // Refresh token every 10 minutes (tokens expire at 15 min, 5 min safety buffer)
      const intervalId = setInterval(refreshVisitorToken, TOKEN_REFRESH_INTERVAL);

      // CRITICAL: Handle page visibility change - refresh token immediately when user returns
      // Browser throttles/suspends timers when tab is inactive, so we need to check on return
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          logger.log('[ChatWidget] Page became visible, checking token...');
          refreshVisitorToken();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Also refresh on window focus (handles alt-tab scenarios)
      const handleFocus = () => {
        logger.log('[ChatWidget] Window focused, checking token...');
        refreshVisitorToken();
      };

      window.addEventListener('focus', handleFocus);

      // Store interval and cleanup functions for removal on unmount
      tokenRefreshIntervalRef.current = {
        interval: intervalId,
        handleVisibilityChange,
        handleFocus
      };
    };

    // Run widget init (which includes tenant-specific config)
    // Only fall back to global config if no tenant context is available
    const widgetCfg = getWidgetConfig();
    initializeWidget();
    if (!widgetCfg?.tenantId && !widgetCfg?.widgetPublicKey) {
      // No tenant context - fetch global config as fallback
      fetchConfig();
    }
  }, [getWidgetConfig]);

  // Note: Tenant config is now embedded in /api/widget/init response
  // The fetchTenantConfig function below is kept as a fallback for edge cases
  // (e.g., when restoring session from existing token without re-initializing)

  // Auto-recovery: Re-initialize when widget becomes uninitialized (e.g., after token failure)
  useEffect(() => {
    if (widgetInitialized) return; // Only run when NOT initialized
    if (initError) return; // Don't retry if there's a permanent error (like domain not allowed)

    const widgetConfig = getWidgetConfig();
    if (!widgetConfig?.widgetPublicKey) return; // Can't re-init without widget key

    // Debounce re-initialization attempts
    const retryTimer = setTimeout(async () => {
      logger.log('[ChatWidget] Auto-recovery: Attempting to re-initialize...');
      try {
        const existingVisitorId = visitorTokenManager.getVisitorId();
        const result = await widgetAPI.initialize(widgetConfig.widgetPublicKey, existingVisitorId);
        if (result.success) {
          logger.log('[ChatWidget] Auto-recovery: Re-initialized successfully');
          setWidgetInitialized(true);
          if (result.sessionId) {
            setSessionId(result.sessionId);
          }
          // Use config from init response
          if (result.config) {
            setConfig(prevConfig => mergeConfigWithDefaults(prevConfig, result.config));
            // Update agent availability (AI chatbot always stays online)
            if (result.config.is_agent_available === false) {
              setIsAgentAvailable(false);
              setAgentOfflineConfig({
                heading: result.config.agent_offline_heading,
                description: result.config.agent_offline_description,
                nextAvailableAt: result.config.agent_next_available_at,
              });
            } else {
              setIsAgentAvailable(true);
              setAgentOfflineConfig(null);
            }
          }
        }
      } catch (error) {
        logger.error('[ChatWidget] Auto-recovery failed:', error);
        // Don't set initError here to allow future retries
      }
    }, 2000); // Wait 2 seconds before retrying

    return () => clearTimeout(retryTimer);
  }, [widgetInitialized, initError, getWidgetConfig]);

  // Save sessionId to localStorage whenever it changes
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('chat_session_id', sessionId);
      // Also send session ID to parent window for persistence across page navigation
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'g6th-sense-session-id', sessionId }, getParentOrigin());
      }
    }
  }, [sessionId]);

  // WebSocket connection for real-time messages
  useEffect(() => {
    if (!sessionId || !widgetInitialized) return;

    const wsBase = BACKEND_URL.replace(/^http/, 'ws');
    const MAX_RECONNECT_ATTEMPTS = 5;
    let reconnectAttempts = 0;

    const connect = () => {
      // Check retry limit to prevent infinite reconnection loop
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        logger.warn('[ChatWidget] Max WebSocket reconnection attempts reached, stopping');
        return;
      }
      reconnectAttempts++;

      // SECURITY FIX: Don't pass token in URL - use message-based auth instead
      // This prevents token exposure in server logs and browser history
      const wsUrl = `${wsBase}/api/ws/chat/${sessionId}`;

      try {
        // Close existing connection if any
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close();
        }

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          logger.log('[ChatWidget] WebSocket connected, sending auth...');
          // Send token via message instead of URL (more secure)
          const token = visitorTokenManager.getToken();
          if (token) {
            ws.send(JSON.stringify({ type: 'auth', token: token }));
          }
        };

        // Handler for resetting reconnect attempts (extracted to reduce nesting)
        const resetReconnectAttempts = (val) => { reconnectAttempts = val; };

        // Handler for processing parsed WebSocket data (extracted to reduce nesting)
        const processWsData = (data) => {
          // Handle auth responses using helper
          if (handleWsAuthMessage(data, resetReconnectAttempts)) {
            return;
          }

          // Handle different message types using helpers
          switch (data.type) {
            case 'new_message':
              if (data.message) handleWsNewMessage(data, typingTimeoutRef, setIsTyping, setMessages);
              break;
            case 'initial_messages':
              if (data.messages) handleWsInitialMessages(data, setMessages, setChatStarted);
              break;
            case 'token_expired':
              handleWsTokenExpired(setTokenVersion);
              break;
            // Ignore keepalive messages (ping, pong, keepalive)
          }
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            processWsData(data);
          } catch (err) {
            logger.error('[ChatWidget] Error parsing message:', err);
          }
        };

        ws.onerror = (error) => {
          logger.error('[ChatWidget] WebSocket error:', error);
        };

        ws.onclose = (event) => {
          logger.log('[ChatWidget] WebSocket closed, code:', event.code, 'reason:', event.reason);
          wsRef.current = null;

          // Handle auth error vs normal close
          if (isWsAuthError(event.code)) {
            handleWsAuthErrorClose(connect, wsReconnectTimeoutRef);
          } else {
            wsReconnectTimeoutRef.current = setTimeout(connect, 3000);
          }
        };

        const pingInterval = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 15000);

        return () => clearInterval(pingInterval);
      } catch (err) {
        logger.error('[ChatWidget] Failed to connect WebSocket:', err);
        wsReconnectTimeoutRef.current = setTimeout(connect, 3000);
      }
    };

    connect();

    return () => {
      if (wsReconnectTimeoutRef.current) clearTimeout(wsReconnectTimeoutRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [sessionId, widgetInitialized, tokenVersion]);

  // Send heartbeat for both visitor presence and session (includes visited_pages for real-time tracking)
  useEffect(() => {
    if (!widgetInitialized) return;

    const visitorId = visitorTokenManager.getVisitorId();

    const sendHeartbeat = async () => {
      // Skip heartbeat when tab is not visible (user switched tabs)
      if (document.visibilityState === 'hidden') {
        return;
      }

      try {
        // Get visitor info including visited_pages (from sessionStorage - current session only)
        const visitorInfo = await getVisitorInfo();

        // Use authenticated client for heartbeat
        await widgetApiClient.post('/api/visitor/heartbeat', {
          visitor_id: visitorId,
          session_id: sessionId || null,
          visitor_info: visitorInfo  // Includes current_page and visited_pages
        });
      } catch (error) {
        logger.error("Error sending heartbeat:", error);
      }
    };

    // Send heartbeat every 30 seconds (sufficient for activity tracking)
    const interval = setInterval(sendHeartbeat, 30000);

    // Send initial heartbeat
    sendHeartbeat();

    return () => clearInterval(interval);
  }, [sessionId, widgetInitialized]);

  // Update visitor tracking on page visibility change (more efficient than polling)
  useEffect(() => {
    if (!sessionId || !widgetInitialized) return;

    let lastTrackTime = 0;
    const MIN_TRACK_INTERVAL = 10000; // Minimum 10 seconds between track calls

    const updateVisitorTracking = async () => {
      // Debounce: don't track more than once every 10 seconds
      const now = Date.now();
      if (now - lastTrackTime < MIN_TRACK_INTERVAL) {
        return;
      }
      lastTrackTime = now;

      try {
        const visitorInfo = await getVisitorInfo();
        // Use authenticated client for tracking
        await widgetApiClient.post('/api/visitor/track', {
          session_id: sessionId,
          visitor_info: visitorInfo
        });
      } catch (error) {
        // Silently ignore tracking errors to avoid console spam
        if (error.response?.status !== 500) {
          logger.error("Error updating visitor tracking:", error);
        }
      }
    };

    // Update tracking when page becomes hidden (user navigates away or closes tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        updateVisitorTracking();
      }
    };

    // Mark visitor as inactive before page unload
    const handleBeforeUnload = () => {
      // Use fetch with keepalive for reliable delivery during page unload
      const token = visitorTokenManager.getToken();
      const visitorId = visitorTokenManager.getVisitorId();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Call leave endpoint to mark visitor as inactive immediately
      fetch(`${API}/visitor/leave`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          visitor_id: visitorId,
          session_id: sessionId || null
        }),
        keepalive: true  // Ensures request completes even after page unloads
      }).catch(() => { }); // Ignore errors during unload
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sessionId, widgetInitialized]);

  const fetchConfig = async () => {
    try {
      // Get global config as fallback defaults only
      const response = await axios.get(`${API}/config`);
      const configData = response.data;
      // Use company_name as fallback for display_name if not set
      if (!configData.display_name && configData.company_name) {
        configData.display_name = configData.company_name;
      }
      // Merge as defaults: global config fills in gaps but never overwrites tenant config
      setConfig(prevConfig => {
        const merged = { ...configData, ...prevConfig };
        // Remove default placeholder values that shouldn't override real values
        if (merged.primary_color === "var(--g6-primary)") {
          merged.primary_color = configData.primary_color;
        }
        if (merged.secondary_color === "var(--g6-secondary)") {
          merged.secondary_color = configData.secondary_color;
        }
        return merged;
      });
    } catch (error) {
      logger.error("Error fetching config:", error);
    }
  };

  // Fetch tenant-specific config after widget is initialized (token is available)
  const fetchTenantConfig = async () => {
    const token = visitorTokenManager.getToken();
    if (!token) {
      logger.log('[ChatWidget] No token available for tenant config');
      return;
    }

    try {
      const response = await axios.get(`${API}/widget/config`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data) {
        // Merge tenant config with existing config (tenant values override)
        setConfig(prevConfig => {
          logger.log('[ChatWidget] Loaded tenant-specific config:', response.data.company_name);
          return mergeConfigWithDefaults(prevConfig, response.data);
        });

        // Update agent availability (AI chatbot always stays online)
        if (response.data.is_agent_available === false) {
          logger.log('[ChatWidget] Agents OFFLINE per schedule — agent handoff will show offline form');
          setIsAgentAvailable(false);
          setAgentOfflineConfig({
            heading: response.data.agent_offline_heading,
            description: response.data.agent_offline_description,
            nextAvailableAt: response.data.agent_next_available_at,
          });
        } else {
          setIsAgentAvailable(true);
          setAgentOfflineConfig(null);
          setShowAgentOfflineForm(false);
        }
      }
    } catch (error) {
      logger.log('[ChatWidget] Could not fetch tenant config:', error.message);
    }
  };

  // Helper functions for widget customization - delegate to module-level functions
  const getPositionClasses = () => getPositionClassesFromConfig(config);
  const getWidgetSize = () => getWidgetSizeFromConfig(config);
  const getBorderRadiusClass = () => getBorderRadiusFromConfig(config);
  const getPrimaryColor = () => config?.primary_color || "var(--g6-primary)";
  const getSecondaryColor = () => config?.secondary_color || "var(--g6-secondary)";
  const isDarkTheme = () => config?.theme === "dark";
  const getBackgroundStyle = () => getBackgroundStyleFromConfig(config);

  const addMessage = (role, content, metadata = {}) => {
    const newId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const now = new Date();
    setMessages((prev) => {
      // Check for duplicate content to prevent double-adding
      // Extended time window to 5 seconds to catch WebSocket/API race conditions
      const isDuplicate = prev.some(
        m => m.content === content && m.role === role &&
          Math.abs(now.getTime() - new Date(m.timestamp).getTime()) < 5000
      );
      if (isDuplicate) {
        logger.log('[ChatWidget] Duplicate message prevented:', role, content.substring(0, 50));
        return prev;
      }
      return [...prev, { id: newId, role, content, timestamp: now, metadata }];
    });
  };

  const simulateTyping = (callback, delay = 1000) => {
    // Clear any existing fallback timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    // Only set typing to true if it's not already true to avoid extra re-renders
    if (!isTyping) setIsTyping(true);

    // Use a managed timeout so we can clear it if another typing action occurs
    const id = setTimeout(() => {
      // Run the callback first so the assistant message is rendered while typing is still visible,
      // then hide the typing indicator to avoid hide-then-show flicker or messages being missed.
      try {
        callback();
      } catch (err) {
        logger.error('[ChatWidget] simulateTyping callback error:', err);
      }

      setIsTyping(false);
      typingTimeoutRef.current = null;
    }, delay);

    typingTimeoutRef.current = id;
  };

  const handleInitialChoice = async (choice) => {
    setFlowType(choice);
    setChatStarted(true);
    setMenuUsed(true);
    setShowInitialMenu(true);
    setShowRestartMenu(false);

    const optionText = getOptionTextForChoice(choice);
    const followUpMessage = getFollowUpMessageForChoice(choice, config, userData);

    // Show the selected option as a USER message (what the user clicked)
    addMessage("user", optionText);

    // Create session if it doesn't exist, or update existing session with flow type
    try {
      // Get full visitor info for session creation
      const visitorInfo = await getVisitorInfo();

      // Generate a new session ID if we don't have one
      const currentSessionId = sessionId || `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Set sessionId immediately if we generated a new one (before API call)
      if (!sessionId) {
        setSessionId(currentSessionId);
      }

      // Use authenticated client for chat API
      const response = await widgetApiClient.post('/api/chat', {
        session_id: currentSessionId,
        message: `[Flow Selected: ${choice}]`,
        flow_type: choice,
        visitor_info: visitorInfo,
        is_flow_selection: true  // Flag to indicate this is just flow selection, not a real message
      });

      // Set sessionId from response (backend confirms/creates the session)
      if (response.data.session_id && !sessionId) {
        setSessionId(response.data.session_id);
        logger.log('[ChatWidget] Session created:', response.data.session_id);
      }
    } catch (error) {
      logger.error("Error creating/updating session:", error);
    }

    // For agent flow, check availability BEFORE showing follow-up message
    if (choice === "agent" && !isAgentAvailable) {
      setShowAgentOfflineForm(true);
      return;
    }

    // Show follow-up question from bot
    simulateTyping(() => {
      addMessage("assistant", followUpMessage);

      if (choice === "agent") {
        if (hasCompleteUserData(userData)) {
          handleExistingUserAgentRequest();
        } else {
          setIsCollectingData(true);
          setDataStep(1);
        }
      }
    });
  };

  const handleExistingUserAgentRequest = async () => {
    if (!sessionId) {
      logger.error('[ChatWidget] handleExistingUserAgentRequest called without sessionId');
      return;
    }

    try {
      // Submit existing user data to trigger agent assignment (use authenticated client)
      const response = await widgetApiClient.post('/api/submit-user-data', {
        session_id: sessionId,
        ...userData
      });

      // Check if agents are offline per schedule (backend guard)
      if (response.data.agents_offline_schedule) {
        setAgentOfflineConfig({
          heading: response.data.agent_offline_heading,
          description: response.data.agent_offline_description,
          nextAvailableAt: response.data.agent_next_available_at,
        });
        setShowAgentOfflineForm(true);
        return;
      }

      // Start waiting for agent response
      setWaitingForAgent(true);
      setAgentTimeoutExceeded(false);
      setSecondsWaiting(0);

      simulateTyping(() => {
        const agentName = response.data.agent_name || "An agent";
        // Show system message that chat has been transferred
        addMessage("system", `Chat transferred to ${agentName}`);
      }, 800);
    } catch (error) {
      logger.error("Error requesting agent:", error);
      addMessage("system", "Connecting you to an agent...");
      // Still set waiting state in case of error
      setWaitingForAgent(true);
    }
  };

  const handleDataCollection = async () => {
    if (!sessionId) {
      logger.error('[ChatWidget] handleDataCollection called without sessionId');
      return;
    }

    if (!inputMessage.trim()) return;
    const value = inputMessage.trim();

    // Handle steps 1-3 using configuration
    const stepConfig = DATA_COLLECTION_STEPS[dataStep];
    if (stepConfig) {
      if (!stepConfig.validate(value)) {
        addMessage("user", inputMessage);
        simulateTyping(() => {
          addMessage("assistant", stepConfig.errorMessage);
        }, 600);
        setInputMessage("");
        return;
      }
      setUserData({ ...userData, [stepConfig.field]: value });
      addMessage("user", inputMessage);
      simulateTyping(() => {
        addMessage("assistant", stepConfig.nextPrompt);
        setDataStep(stepConfig.nextStep);
      }, 800);
      setInputMessage("");
      return;
    }

    // Handle step 4 (final submission)
    if (dataStep === 4) {
      const finalUserData = { ...userData, context: inputMessage };
      addMessage("user", inputMessage);

      try {
        setIsLoading(true);
        const response = await widgetApiClient.post('/api/submit-user-data', {
          session_id: sessionId,
          ...finalUserData
        });

        setUserData(finalUserData);

        // Check if agents are offline per schedule (backend guard)
        if (response.data.agents_offline_schedule) {
          setIsCollectingData(false);
          setDataStep(0);
          setAgentOfflineConfig({
            heading: response.data.agent_offline_heading,
            description: response.data.agent_offline_description,
            nextAvailableAt: response.data.agent_next_available_at,
          });
          setShowAgentOfflineForm(true);
          setInputMessage("");
          return;
        }

        setWaitingForAgent(true);
        setAgentTimeoutExceeded(false);
        setSecondsWaiting(0);

        simulateTyping(() => {
          const agentName = response.data.agent_name || "An agent";
          addMessage("system", `Chat transferred to ${agentName}`);
          setIsCollectingData(false);
          setDataStep(0);
        }, 1200);
        setInputMessage("");
      } catch (error) {
        logger.error("Error submitting user data:", error);
        const errorMsg = extractApiErrorMessage(
          error,
          "I apologize, but there was an error submitting your information. Please try again or contact us directly."
        );
        addMessage("assistant", errorMsg);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle agent timeout - transfer back to AI
  const handleTransferToAI = async () => {
    if (!sessionId) return;

    try {
      // Use authenticated client for session management
      await widgetApiClient.post('/api/session/reassign-to-ai', {
        session_id: sessionId
      });

      // Reset waiting state
      setWaitingForAgent(false);
      setAgentTimeoutExceeded(false);
      setSecondsWaiting(0);

      addMessage("system", "Chat transferred back to AI Assistant");
    } catch (error) {
      logger.error("Error transferring to AI:", error);
      addMessage("system", "Unable to transfer. Please try again.");
    }
  };

  // Handle user choosing to keep waiting for agent
  const handleKeepWaiting = () => {
    setAgentTimeoutExceeded(false);
    // Continue waiting - the interval will keep checking
    addMessage("system", "No problem! We'll notify you when an agent responds.");
  };

  // Configuration for streaming vs non-streaming mode
  const USE_STREAMING = false;

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    if (isCollectingData) {
      await handleDataCollection();
      return;
    }

    const userMessage = inputMessage;

    // Check for main menu request
    if (isMainMenuRequest(userMessage)) {
      addMessage("user", userMessage);
      setInputMessage("");
      setShowFollowUp(false);
      setSessionClosed(false);
      setTimeout(() => {
        setShowMainMenu(true);
      }, 300);
      return;
    }

    // Hide follow-up question when user sends a new message
    setShowFollowUp(false);
    setShowMainMenu(false);
    setSessionClosed(false);

    // Show typing indicator immediately when user sends a message
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    setIsTyping(true);

    addMessage("user", userMessage);
    setInputMessage("");
    setIsLoading(true);

    // Generate session ID if we don't have one yet
    let currentSessionId = sessionId;
    if (!currentSessionId) {
      currentSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(currentSessionId);
      logger.log('[ChatWidget] Session created on send:', currentSessionId);
    }

    try {
      // Use full visitor info if creating a new session, minimal otherwise
      const visitorInfo = sessionId ? getMinimalVisitorInfo() : await getVisitorInfo();

      if (USE_STREAMING) {
        // Use streaming endpoint for real-time response
        await handleStreamingMessage(userMessage, visitorInfo, currentSessionId);
      } else {
        // Use traditional non-streaming endpoint
        await handleNonStreamingMessage(userMessage, visitorInfo, currentSessionId);
      }
    } catch (error) {
      logger.error("Error sending message:", error);
      addMessage("assistant", "I apologize, but I'm having trouble connecting right now. Please try again or contact us directly.");
      setIsTyping(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStreamingMessage = async (userMessage, visitorInfo, currentSessionId) => {
    const tempMessageId = `streaming-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const streamState = { content: '', messageAdded: false };

    try {
      const response = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: buildStreamHeaders(),
        body: JSON.stringify({
          session_id: currentSessionId,
          message: userMessage,
          flow_type: flowType,
          visitor_info: visitorInfo
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const parsedEvents = parseSSELines(chunk);
        parsedEvents.forEach(data => processStreamEvent(data, tempMessageId, streamState));
      }
    } catch (error) {
      logger.error('[ChatWidget] Streaming error:', error);
      setIsTyping(false);
      if (!streamState.messageAdded) {
        addMessage("assistant", "I apologize, but I'm having trouble connecting right now. Please try again or contact us directly.");
      }
    }
  };

  // Process individual stream events to reduce nesting depth
  const processStreamEvent = (data, tempMessageId, streamState) => {
    switch (data.type) {
      case 'start':
        setIsTyping(false);
        logger.log('[ChatWidget] Streaming started, response_id:', data.response_id);
        break;

      case 'content':
        streamState.content += data.content;
        updateStreamingMessage(tempMessageId, streamState);
        break;

      case 'done':
        finalizeStreamingMessage(tempMessageId, data);
        break;

      case 'agent_active':
        setIsTyping(false);
        logger.log('[ChatWidget] Agent is handling this conversation');
        break;

      case 'error':
        logger.error('[ChatWidget] Stream error:', data.message);
        if (!streamState.messageAdded) {
          addMessage("assistant", "I apologize, but I'm having trouble processing your request. Please try again.");
        }
        break;
    }
  };

  // Update streaming message content
  const updateStreamingMessage = (tempMessageId, streamState) => {
    if (!streamState.messageAdded) {
      setMessages(prev => [...prev, {
        id: tempMessageId,
        role: 'assistant',
        content: streamState.content,
        timestamp: new Date(),
        isStreaming: true
      }]);
      streamState.messageAdded = true;
    } else {
      setMessages(prev =>
        prev.map(msg => msg.id === tempMessageId ? { ...msg, content: streamState.content } : msg)
      );
    }
  };

  // Finalize streaming message and handle post-stream actions
  const finalizeStreamingMessage = (tempMessageId, data) => {
    setMessages(prev =>
      prev.map(msg => msg.id === tempMessageId ? { ...msg, isStreaming: false } : msg)
    );

    if (data.keyword_triggered) {
      // Check if agents are offline per schedule (from backend response)
      if (data.agents_offline_schedule) {
        setAgentOfflineConfig({
          heading: data.agent_offline_heading,
          description: data.agent_offline_description,
          nextAvailableAt: data.agent_next_available_at,
        });
        setShowAgentOfflineForm(true);
      } else if (hasCompleteUserData(userData)) {
        handleExistingUserAgentRequest();
      } else {
        setIsCollectingData(true);
        setDataStep(1);
      }
    }

    if (data.session_ended) {
      logger.log('[ChatWidget] Session ended - user said goodbye');
      setTimeout(() => setSessionClosed(true), 2000);
    } else if (!data.keyword_triggered) {
      setTimeout(() => setShowFollowUp(true), 500);
    }

    logger.log('[ChatWidget] Streaming completed');
  };

  const handleNonStreamingMessage = async (userMessage, visitorInfo, currentSessionId) => {
    // Use authenticated client for chat API
    const response = await widgetApiClient.post('/api/chat', {
      session_id: currentSessionId,
      message: userMessage,
      flow_type: flowType,
      visitor_info: visitorInfo
    });

    // Update sessionId if backend confirms a different one
    if (response.data.session_id && response.data.session_id !== currentSessionId) {
      logger.log('[ChatWidget] Backend confirmed session_id:', response.data.session_id);
      setSessionId(response.data.session_id);
    }

    if (response.data.agent_active) {
      // Agent is handling - don't add any bot message
      // The message was sent to the server and agent will see it
      logger.log("Agent is handling this conversation - message sent");
      setIsTyping(false);
    } else if (response.data.response) {
      addMessage("assistant", response.data.response);
      // Hide typing indicator after adding the message
      setIsTyping(false);

      // Show follow-up question after bot response (unless keyword triggered or session ended)
      if (!response.data.keyword_triggered && !response.data.session_ended) {
        setTimeout(() => {
          setShowFollowUp(true);
        }, 500);
      }
    } else {
      // No response, hide typing indicator
      setIsTyping(false);
    }

    // Check if keyword triggered agent handoff
    if (response.data.keyword_triggered) {
      if (response.data.agents_offline_schedule) {
        // Agents offline per schedule — show inline offline form
        setAgentOfflineConfig({
          heading: response.data.agent_offline_heading,
          description: response.data.agent_offline_description,
          nextAvailableAt: response.data.agent_next_available_at,
        });
        setShowAgentOfflineForm(true);
      } else if (response.data.action === "collect_user_data") {
        if (hasCompleteUserData(userData)) {
          handleExistingUserAgentRequest();
        } else {
          setIsCollectingData(true);
          setDataStep(1);
        }
      }
    }

    // Check if session ended (user said goodbye)
    if (response.data.session_ended) {
      logger.log('[ChatWidget] Session ended - user said goodbye');
      // Show the "Let's Chat" button after a delay
      setTimeout(() => {
        setSessionClosed(true);
      }, 2000); // 2 second delay to let user see goodbye message
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (embedded && !isOpen) {
    // Don't render button until config is loaded to prevent color flash
    if (!config) {
      return null;
    }

    const buttonText = config?.button_text;
    const bgColor = getPrimaryColor();
    const bgStyle = getBackgroundStyle();

    return (
      <button
        data-testid="chat-widget-button"
        onClick={() => setIsOpen(true)}
        className={`fixed ${getPositionClasses()} ${buttonText ? 'px-5 py-3' : 'w-16 h-16'} text-white ${getBorderRadiusClass()} shadow-2xl transition-all duration-300 hover:scale-110 z-50 flex items-center justify-center gap-2 group`}
        style={{
          ...bgStyle,
          boxShadow: `0 25px 50px -12px ${bgColor}40`
        }}
        aria-label="Open chat"
      >
        <MessageCircle size={buttonText ? 22 : 28} className="group-hover:scale-110 transition-transform" />
        {buttonText && <span className="font-medium text-sm">{buttonText}</span>}
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse border-2 border-white"></span>
      </button>
    );
  }

  // Don't render the main widget until session loading is complete
  // Note: sessionId can be null for new visitors - that's OK, session is created when they first chat
  if (!sessionLoaded) {
    const { width, height } = getWidgetSize();
    return (
      <div className={`${embedded ? `fixed ${getPositionClasses()} ${width} ${height} shadow-2xl z-50 ${getBorderRadiusClass()} border-2 border-gray-200` : 'w-full h-screen'} flex items-center justify-center bg-white`}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: `${getPrimaryColor()}33`, borderTopColor: 'transparent' }}>
            <div className="w-full h-full border-4 border-transparent rounded-full" style={{ borderTopColor: getPrimaryColor() }}></div>
          </div>
          <p className="text-gray-600 text-sm">Loading chat...</p>
        </div>
      </div>
    );
  }

  // Show error state if widget initialization failed
  if (initError) {
    const { width, height } = getWidgetSize();
    return (
      <div className={`${embedded ? `fixed ${getPositionClasses()} ${width} ${height} shadow-2xl z-50 ${getBorderRadiusClass()} border-2 border-red-200` : 'w-full h-screen'} flex items-center justify-center bg-white`}>
        <div className="flex flex-col items-center gap-3 p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <X size={24} className="text-red-500" />
          </div>
          <p className="text-gray-800 font-medium">Unable to Load Chat</p>
          <p className="text-gray-500 text-sm">{initError}</p>
          {embedded && (
            <button
              onClick={() => setIsOpen(false)}
              className="mt-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  const { width, height } = getWidgetSize();

  return (
    <div
      data-testid="chat-widget"
      className={`${embedded
        ? `fixed ${getPositionClasses()} ${width} ${height} shadow-2xl z-50 animate-in slide-in-from-bottom-4 duration-300 ${getBorderRadiusClass()} border-2 ${isDarkTheme() ? 'border-gray-700' : 'border-gray-200'}`
        : "w-full h-screen"
        } flex flex-col ${isDarkTheme() ? 'bg-gray-900' : 'bg-white'} overflow-hidden`}
    >
      {/* Header */}
      <div
        className={`p-5 border-b flex-shrink-0 ${isDarkTheme() ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center">
              <img
                src={config?.logo_url || "/Logo.png"}
                alt={`${config?.display_name || "Company"} Logo`}
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h3 className={`font-bold text-lg ${isDarkTheme() ? 'text-gray-100' : 'text-gray-900'}`} data-testid="chat-title">
                {config?.display_name || config?.company_name || "Assistant"}
              </h3>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <p className={`text-xs ${isDarkTheme() ? 'text-gray-400' : 'text-gray-500'}`}>Online</p>
              </div>
            </div>
          </div>
          <div className="flex space-x-2">
            {embedded ? (
              <>
                <button
                  data-testid="minimize-chat"
                  onClick={() => setIsOpen(false)}
                  className={`${isDarkTheme() ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} p-2.5 rounded-lg transition-all ${isDarkTheme() ? 'text-gray-400' : 'text-gray-600'}`}
                  aria-label="Minimize chat"
                >
                  <Minimize2 size={18} />
                </button>
                <button
                  data-testid="close-chat"
                  onClick={() => {
                    if (sessionId && messages.length > 0) {
                      setShowCloseConfirm(true);
                    } else {
                      setIsOpen(false);
                    }
                  }}
                  className={`${isDarkTheme() ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} p-2.5 rounded-lg transition-all ${isDarkTheme() ? 'text-gray-400' : 'text-gray-600'}`}
                  aria-label="Close chat"
                >
                  <X size={18} />
                </button>
              </>
            ) : (
              /* Close button for iframe mode - shows confirmation if messages exist */
              <button
                data-testid="close-iframe-chat"
                onClick={() => {
                  // Show confirmation if there are messages, otherwise close directly
                  if (sessionId && messages.length > 0) {
                    setShowCloseConfirm(true);
                  } else {
                    // Send message to parent window to close the widget
                    if (window.parent !== window) {
                      window.parent.postMessage('g6th-sense-close-widget', getParentOrigin());
                    }
                  }
                }}
                className={`${isDarkTheme() ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} p-2.5 rounded-lg transition-all ${isDarkTheme() ? 'text-gray-400' : 'text-gray-600'}`}
                aria-label="Close chat"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* GDPR Geolocation Consent Dialog */}
      {showGeoConsent && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 rounded-3xl animate-in fade-in duration-200">
          <div className={`${isDarkTheme() ? 'bg-gray-800' : 'bg-white'} p-6 rounded-2xl max-w-sm mx-4 shadow-2xl animate-in zoom-in-95 duration-200`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${getPrimaryColor()}20` }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={getPrimaryColor()} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="10" r="3" />
                  <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 6.9 8 11.7z" />
                </svg>
              </div>
              <h3 className={`text-lg font-bold ${isDarkTheme() ? 'text-gray-100' : 'text-gray-900'}`}>Location Access</h3>
            </div>
            <p className={`mb-5 text-sm leading-relaxed ${isDarkTheme() ? 'text-gray-300' : 'text-gray-600'}`}>
              We'd like to collect your location data to provide a better, more personalized experience. This helps us:
            </p>
            <ul className={`mb-5 text-sm space-y-1 ${isDarkTheme() ? 'text-gray-400' : 'text-gray-500'}`}>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> Show relevant local information
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> Connect you with nearby support
              </li>
            </ul>
            <p className={`mb-5 text-xs ${isDarkTheme() ? 'text-gray-500' : 'text-gray-400'}`}>
              You can change this setting anytime. We respect your privacy and comply with GDPR.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setGeoConsentStatus(false);
                  setShowGeoConsent(false);
                }}
                className={`flex-1 px-4 py-2.5 ${isDarkTheme() ? 'bg-gray-700 hover:bg-gray-600 text-gray-100' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} rounded-xl transition-colors font-medium text-sm`}
              >
                No thanks
              </button>
              <button
                onClick={() => {
                  setGeoConsentStatus(true);
                  setShowGeoConsent(false);
                }}
                className="flex-1 px-4 py-2.5 text-white rounded-xl transition-all font-medium text-sm shadow-lg hover:opacity-90"
                style={getBackgroundStyle()}
              >
                Allow
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Confirmation Dialog */}
      {showCloseConfirm && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 rounded-3xl animate-in fade-in duration-200">
          <div className={`${isDarkTheme() ? 'bg-gray-800' : 'bg-white'} p-7 rounded-2xl max-w-sm mx-4 shadow-2xl animate-in zoom-in-95 duration-200`}>
            <h3 className={`text-xl font-bold mb-3 ${isDarkTheme() ? 'text-gray-100' : 'text-gray-900'}`}>Close Chat?</h3>
            <p className={`mb-6 leading-relaxed ${isDarkTheme() ? 'text-gray-300' : 'text-gray-600'}`}>
              Are you sure you want to close this conversation? Your chat history will be saved.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className={`flex-1 px-5 py-3 ${isDarkTheme() ? 'bg-gray-700 hover:bg-gray-600 text-gray-100' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'} rounded-xl transition-colors font-medium`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowCloseConfirm(false);

                  // End session on server using authenticated client
                  if (sessionId) {
                    try {
                      await widgetApiClient.post('/api/session/end', { session_id: sessionId });
                    } catch (error) {
                      logger.error('[ChatWidget] Error ending session:', error);
                    }
                  }

                  // Clear session ID but keep visitor token (so we can fetch history on refresh)
                  localStorage.removeItem('chat_session_id');
                  visitorTokenManager.clearSessionId();
                  // DON'T clear visitor token - we need it to fetch chat history
                  // visitorTokenManager.clearToken();

                  // In iframe mode, send message to parent to close widget
                  if (!embedded && window.parent !== window) {
                    window.parent.postMessage('g6th-sense-end-session', getParentOrigin());
                  } else {
                    setIsOpen(false);
                  }

                  // Show expired session UI - keep messages visible with "Let's chat" button
                  setSessionId(null);
                  setLastSessionExpired(true);
                  setSessionEndReason('visitor_closed');
                  setShowRestartMenu(false);
                  setMenuUsed(false);
                  setShowInitialMenu(false);
                  setShowFollowUp(false);
                  // Keep messages and chatStarted so history is visible
                }}
                className="flex-1 px-5 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all font-medium shadow-lg"
              >
                Close Chat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages — always visible (AI chatbot is always online) */}
      <>
      <div className={`flex-1 overflow-y-auto min-h-0 p-5 space-y-4 ${isDarkTheme() ? 'bg-gray-800' : 'bg-gradient-to-b from-gray-50 to-white'}`}>
        {/* Welcome Card with Logo and Options - Shows when chat hasn't started */}
        {messages.length === 0 && !chatStarted && !sessionClosed && (
          <WelcomeCard
            isDark={isDarkTheme()}
            config={config}
            primaryColor={getPrimaryColor()}
            onChoice={handleInitialChoice}
          />
        )}

        {/* Initial greeting when chatStarted but no messages yet (e.g. expired session with no history) */}
        {messages.length === 0 && chatStarted && !menuUsed && (
          <div className="flex justify-start animate-in fade-in duration-500">
            <div className="flex items-start gap-3 max-w-[85%]">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: getPrimaryColor() }}
              >
                <Bot size={16} className="text-white" />
              </div>
              <div className={`${isDarkTheme() ? 'bg-gray-700 text-gray-100' : 'bg-white text-gray-800'} p-4 rounded-2xl rounded-bl-md shadow-md`}>
                <p className="leading-relaxed">
                  {config?.greeting_message || "Hello! How can I help you today?"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Static greeting + menu bubble for fresh interactions (persists after user selects a flow) */}
        {showInitialMenu && !showRestartMenu && !lastSessionExpired && !sessionClosed && (
          <>
            <BotGreetingBubble isDark={isDarkTheme()} primaryColor={getPrimaryColor()} />
            <StaticMenuBubble isDark={isDarkTheme()} config={config} primaryColor={getPrimaryColor()} />
          </>
        )}

        {/* Render messages with greeting/menu inserted after history messages */}
        {messages.map((msg, index) => (
          <React.Fragment key={msg.id || `${msg.role}-${msg.timestamp || Date.now()}-${index}`}>
            {/* Insert session ended notice, greeting and menu AFTER the last history message */}
            {index === historyMessageCount && historyMessageCount > 0 && menuUsed && !showRestartMenu && (
              <>
                <SessionDivider isDark={isDarkTheme()} text={getSessionEndReasonText(sessionEndReason)} />
                <BotGreetingBubble isDark={isDarkTheme()} primaryColor={getPrimaryColor()} />
                <StaticMenuBubble isDark={isDarkTheme()} config={config} primaryColor={getPrimaryColor()} />
              </>
            )}

            {/* Regular message rendering */}
            {msg.role === "system" ? (
              <SystemMessage msg={msg} isDark={isDarkTheme()} />
            ) : (
              <div
                data-testid={`message-${msg.role}`}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"
                  } animate-in fade-in slide-in-from-${msg.role === "user" ? "right" : "left"}-4 duration-300`}
              >
                <div
                  className={`max-w-[85%] ${msg.role === "user"
                    ? `text-white ${getBorderRadiusClass()} rounded-br-md shadow-lg`
                    : `${isDarkTheme() ? 'bg-gray-700 text-gray-100 border-gray-600' : 'bg-white text-gray-800 border-gray-100'} ${getBorderRadiusClass()} rounded-bl-md shadow-md border`
                    } p-4`}
                  style={msg.role === "user" ? getBackgroundStyle() : {}}
                >
                  <MessageHeader
                    role={msg.role}
                    isDark={isDarkTheme()}
                    primaryColor={getPrimaryColor()}
                    agentName={msg.metadata?.agent_name}
                  />
                  <MessageContent msg={msg} primaryColor={getPrimaryColor()} />
                  <MessageTimestamp msg={msg} />
                </div>
              </div>
            )}
          </React.Fragment>
        ))}

        {/* Session Expired Notice - only show when lastSessionExpired is true */}
        {lastSessionExpired && chatStarted && !sessionClosed && (
          <SessionDivider isDark={isDarkTheme()} text={getSessionEndReasonText(sessionEndReason)} />
        )}

        {/* Bot greeting message - Shows when restart menu is visible (after clicking Let's chat) */}
        {showRestartMenu && chatStarted && !lastSessionExpired && (
          <BotGreetingBubble isDark={isDarkTheme()} primaryColor={getPrimaryColor()} />
        )}

        {/* Restart Menu Bubble - Shows after clicking "Let's chat" with history preserved */}
        {/* Interactive version - shows when showRestartMenu is true AND session is not expired */}
        {showRestartMenu && chatStarted && !lastSessionExpired && (
          <RestartMenuBubble
            isDark={isDarkTheme()}
            config={config}
            primaryColor={getPrimaryColor()}
            onChoice={handleInitialChoice}
          />
        )}

        {isTyping && config?.show_typing_indicator !== false && (
          <TypingIndicator
            isDark={isDarkTheme()}
            primaryColor={getPrimaryColor()}
            borderRadiusClass={getBorderRadiusClass()}
          />
        )}

        {/* Follow-up question after bot response */}
        {showFollowUp && !isTyping && !isCollectingData && !waitingForAgent && (
          <FollowUpQuestion
            isDark={isDarkTheme()}
            primaryColor={getPrimaryColor()}
            backgroundStyle={getBackgroundStyle()}
            borderRadiusClass={getBorderRadiusClass()}
            onYes={() => {
              setShowFollowUp(false);
              addMessage("user", "Yes");
              simulateTyping(() => {
                addMessage("assistant", "What would you like to know?");
              }, 800);
            }}
            onNo={() => {
              setShowFollowUp(false);
              addMessage("user", "No. I am good.");
              simulateTyping(() => {
                addMessage("assistant", "I am glad that I was able to assist you. Thank You.");
                setTimeout(() => {
                  addMessage("system", `${config?.company_name || 'AI'} Assistant has closed the chat.`);
                  setSessionClosed(true);
                }, 1000);
              }, 800);
            }}
          />
        )}

        {/* Session Closed - Let's Chat button */}
        {sessionClosed && !showMainMenu && (
          <SessionClosedCard
            isDark={isDarkTheme()}
            primaryColor={getPrimaryColor()}
            backgroundStyle={getBackgroundStyle()}
            onRestart={() => {
              setSessionClosed(false);
              setMessages([]);
              setChatStarted(false);
              setFlowType(null);
              setSessionId(null);
              setShowFollowUp(false);
              setMenuUsed(false);
              setShowInitialMenu(false);
              setShowRestartMenu(false);
            }}
          />
        )}

        {/* Main Menu Options - Shows when user asks for main menu */}
        {showMainMenu && (
          <WelcomeCard
            isDark={isDarkTheme()}
            config={config}
            primaryColor={getPrimaryColor()}
            onChoice={(choice) => {
              setShowMainMenu(false);
              handleInitialChoice(choice);
            }}
          />
        )}

        {/* Agent Offline Form — shown inline in chat when user tries to talk to agent outside schedule */}
        {showAgentOfflineForm && (
          <OfflineForm
            config={config}
            isDark={isDarkTheme()}
            primaryColor={getPrimaryColor()}
            backgroundStyle={getBackgroundStyle()}
            nextAvailableAt={agentOfflineConfig?.nextAvailableAt}
            heading={agentOfflineConfig?.heading || "Our agents are currently offline"}
            description={agentOfflineConfig?.description || "Leave us a message and an agent will get back to you, or continue chatting with our AI assistant!"}
            onContinueChat={() => {
              setShowAgentOfflineForm(false);
              setFlowType("information");
              setIsCollectingData(false);
              setDataStep(0);
              addMessage("assistant", "No problem! I'm here to help. What would you like to know?");
            }}
            inline
          />
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Agent Timeout Options */}
      {agentTimeoutExceeded && (
        <AgentTimeoutOptions
          isDark={isDarkTheme()}
          onKeepWaiting={handleKeepWaiting}
          onTransferToAI={handleTransferToAI}
          backgroundStyle={getBackgroundStyle()}
        />
      )}

      {/* Input Area - Shows different content based on session state */}
      {chatStarted && (
        <div className={`p-5 flex-shrink-0 ${isDarkTheme() ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} border-t-2`}>
          {/* Show "Hello. How may I help you?" and "Let's chat" button when session is expired */}
          {lastSessionExpired && !showRestartMenu ? (
            <ExpiredSessionInput
              isDark={isDarkTheme()}
              primaryColor={getPrimaryColor()}
              backgroundStyle={getBackgroundStyle()}
              onRestart={() => {
                setLastSessionExpired(false);
                setSessionEndReason(null);
                setShowRestartMenu(true);
                setMenuUsed(false);
                setShowInitialMenu(false);
                setSessionId(null);
                setShowFollowUp(false);
                setTimeout(() => scrollToBottom(), 100);
              }}
            />
          ) : (
            /* Normal input area */
            <>
              <div className="flex space-x-3 items-end">
                <textarea
                  ref={inputRef}
                  data-testid="chat-input"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    isCollectingData
                      ? "Type your response..."
                      : "Type your message..."
                  }
                  rows={1}
                  className={`flex-1 border-2 ${isDarkTheme() ? 'border-gray-600 bg-gray-700 text-gray-100 placeholder:text-gray-400' : 'border-gray-200 bg-white text-gray-900 placeholder:text-gray-400'} rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:border-transparent transition-all resize-none overflow-hidden`}
                  style={{
                    '--tw-ring-color': getPrimaryColor(),
                    minHeight: '48px',
                    maxHeight: '120px'
                  }}
                  disabled={isLoading}
                  onInput={(e) => {
                    // Auto-resize textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                />
                <button
                  data-testid="send-message"
                  onClick={handleSendMessage}
                  disabled={isLoading || !inputMessage.trim()}
                  className="text-white p-3.5 rounded-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:scale-105 disabled:hover:scale-100"
                  style={{
                    ...getBackgroundStyle(),
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading && inputMessage.trim()) {
                      e.currentTarget.style.opacity = '0.85';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                  aria-label="Send message"
                >
                  <Send size={20} />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2 text-center">
                Powered by <span className="font-semibold">G6th Sense</span>
              </p>
            </>
          )}
        </div>
      )}
      </>
    </div>
  );
};

export default ChatWidget;
